import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import alert from '../../helpers/alert';
import { socket } from '../../App';
import createClientUploadLink from '../../api/clientUpload/createClientUploadLink';
import getClientUploadStatus from '../../api/clientUpload/getClientUploadStatus';
import sendClientUploadSms from '../../api/clientUpload/sendClientUploadSms';
import revokeClientUploadLink from '../../api/clientUpload/revokeClientUploadLink';
import { ALL_KINDS, CLIENT_UPLOAD_KINDS, defaultKinds, receivedByKind } from '../../pages/Public/clientUploadKinds';

const POLL_MS = 10000;
// Пока окно открыто и менеджер ждёт клиента — спрашиваем чаще.
const OPEN_POLL_MS = 3000;

// Что клиент уже прислал по сделке. Опрос — страховка на случай, если сокет не
// доставил событие; само событие и возврат на вкладку обновляют сразу.
// Возвращает [что_прислано, обновить_сейчас].
export const useClientUploads = (dealId) => {
  const [received, setReceived] = useState({});

  const refresh = useCallback(() => {
    if (!dealId) return;
    getClientUploadStatus({ dealId })
      .then((res) => setReceived(receivedByKind(res?.data?.files)))
      .catch(() => {});
  }, [dealId]);

  useEffect(() => {
    if (!dealId) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    const channel = 'listenClientUploadInDeal' + dealId;
    socket.on(channel, refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      socket.off(channel, refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dealId, refresh]);

  return [received, refresh];
};

const MUTED = '#777';
const GREEN = '#1a8f3c';
const BORDER = '#e2e2e2';

const linkButton = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  color: 'var(--brand-primary, #1a73e8)',
  fontSize: 14,
  fontWeight: 600,
};

const outlineButton = {
  background: '#fff',
  color: 'var(--brand-primary)',
  border: '1px solid var(--brand-primary)',
};

const QrIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <path d="M14 14h3v3h-3zM20 14v1M14 20h1M18 18h3v3h-3z" />
  </svg>
);

const StatusMark = ({ done }) => (
  <span
    aria-hidden="true"
    style={{
      width: 20,
      height: 20,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      color: '#fff',
      background: done ? GREEN : 'transparent',
      border: done ? 'none' : `2px solid ${BORDER}`,
    }}
  >
    {done ? '✓' : ''}
  </span>
);

const OpenLink = ({ file }) => (
  <a href={file.url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
    открыть
  </a>
);

// Кнопка в шапке блока «Сканы документов» и окно менеджера: выбрать, что
// запросить → показать QR / отправить ссылку → видеть, что уже прислал клиент.
const ClientUploadBlock = ({ dealId, phone, showIncome, showWork, received, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [qr, setQr] = useState('');
  const [picked, setPicked] = useState([]);
  const [smsBusy, setSmsBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    if (!isOpen || !onRefresh) return undefined;
    onRefresh();
    const timer = setInterval(onRefresh, OPEN_POLL_MS);
    return () => clearInterval(timer);
  }, [isOpen, onRefresh]);

  const open = () => {
    setLink(null);
    setConfirmOff(false);
    setPicked(defaultKinds({ showIncome, showWork, received }));
    setIsOpen(true);
  };

  const toggle = (kind) =>
    setPicked((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));

  const create = async () => {
    setLoading(true);
    try {
      const res = await createClientUploadLink({ dealId, kinds: picked });
      setLink(res.data);
      setQr(await QRCode.toDataURL(res.data.url, { width: 360, margin: 1 }));
    } catch (e) {
      alert.error('Не удалось создать ссылку для клиента');
    } finally {
      setLoading(false);
    }
  };

  // Номер бэк берёт из карточки клиента сам; ошибки (лимит, нет номера) показывает общий обработчик.
  const sendSms = async () => {
    setSmsBusy(true);
    try {
      const res = await sendClientUploadSms({ dealId, kinds: link.kinds });
      alert.success(`СМС со ссылкой отправлено клиенту (номер на …${res.data.phoneTail})`);
    } catch (e) {
      // уже показано в plugins/api
    } finally {
      setSmsBusy(false);
    }
  };

  // Приём закрыт: ссылка и QR больше не работают, уже присланные файлы остаются в анкете.
  const closeLink = async () => {
    try {
      await revokeClientUploadLink({ dealId });
      alert.success('Ссылка отключена — клиент больше не может загружать документы');
      setIsOpen(false);
    } catch (e) {
      // ошибку уже показал plugins/api
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(link.url).then(
      () => alert.success('Ссылка скопирована'),
      () => alert.error('Не удалось скопировать — выделите ссылку вручную')
    );
  };

  const digits = String(phone || '').replace(/\D/g, '');
  const whatsapp = link
    ? `https://wa.me/${digits}?text=${encodeURIComponent('Документы для заявки (загрузите с телефона): ' + link.url)}`
    : '';

  // Всё, что просили в этой ссылке, плюс всё, что клиент прислал раньше.
  const shownKinds = link ? ALL_KINDS.filter((k) => link.kinds.includes(k) || received?.[k]) : [];
  const allReceived = !!link && link.kinds.every((k) => received?.[k]);
  const missingTitles = link ? link.kinds.filter((k) => !received?.[k]).map((k) => CLIENT_UPLOAD_KINDS[k].managerTitle) : [];

  if (!dealId) return null;

  return (
    <>
      <button type="button" style={linkButton} onClick={open}>
        <QrIcon />
        Запросить у клиента
      </button>
      <Modal
        width={480}
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        title="Документы от клиента"
        contentStyle={{ bottom: 'auto', height: 'auto', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'grid', gap: 16 }}>
          {!link ? (
            <>
              <p style={{ margin: 0, color: MUTED }}>
                Клиент откроет страницу по ссылке и загрузит файлы сам — прямо со своего телефона. Что нужно запросить?
              </p>
              <div style={{ display: 'grid', gap: 8 }}>
                {ALL_KINDS.map((kind) => (
                  <label
                    key={kind}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      border: `1px solid ${picked.includes(kind) ? 'var(--brand-primary)' : BORDER}`,
                      cursor: 'pointer',
                    }}
                  >
                    <input type="checkbox" checked={picked.includes(kind)} onChange={() => toggle(kind)} />
                    <span style={{ flex: 1 }}>{CLIENT_UPLOAD_KINDS[kind].managerTitle}</span>
                    {received?.[kind] && (
                      <span style={{ fontSize: 14, color: GREEN }}>
                        ✓ уже есть · <OpenLink file={received[kind]} />
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <Button type="button" fill full onClick={create} disabled={loading || picked.length === 0}>
                {picked.length === 0 ? 'Отметьте хотя бы один документ' : 'Получить ссылку и QR'}
              </Button>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', justifyItems: 'center', gap: 6 }}>
                {qr && <img src={qr} alt="QR для клиента" style={{ width: 180, height: 180 }} />}
                <span style={{ color: MUTED, fontSize: 14 }}>Клиент наводит камеру телефона на QR-код</span>
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                <span style={{ color: MUTED, fontSize: 14 }}>или отправьте ссылку</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    readOnly
                    value={link.url}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: 1, minWidth: 0, padding: '0 12px', border: `1px solid ${BORDER}`, font: 'inherit', fontSize: 14 }}
                  />
                  <Button type="button" onClick={copy} style={{ ...outlineButton, padding: '10px 18px' }}>
                    Копировать
                  </Button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: digits ? '1fr 1fr' : '1fr', gap: 8 }}>
                  <Button type="button" full onClick={sendSms} disabled={smsBusy} style={outlineButton}>
                    {smsBusy ? 'Отправляем…' : 'Отправить СМС'}
                  </Button>
                  {digits && (
                    <a href={whatsapp} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                      <Button type="button" full style={outlineButton}>
                        WhatsApp
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10, paddingTop: 14, borderTop: `1px solid ${BORDER}` }}>
                <b>Что прислал клиент</b>
                {shownKinds.map((k) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <StatusMark done={!!received?.[k]} />
                    <span style={{ flex: 1 }}>{CLIENT_UPLOAD_KINDS[k].managerTitle}</span>
                    {received?.[k] ? (
                      <span style={{ fontSize: 14, color: GREEN }}>
                        получено · <OpenLink file={received[k]} />
                      </span>
                    ) : (
                      <span style={{ fontSize: 14, color: MUTED }}>ждём клиента…</span>
                    )}
                  </div>
                ))}
                {allReceived ? (
                  <div style={{ background: '#e6f4ea', padding: '10px 12px', fontSize: 14 }}>
                    Клиент прислал всё. Файлы уже в анкете — их можно отправлять в банки.
                  </div>
                ) : (
                  <span style={{ color: MUTED, fontSize: 14 }}>
                    Список обновляется сам. Ссылка действует 24 часа.
                  </span>
                )}
              </div>

              {confirmOff ? (
                <div style={{ display: 'grid', gap: 10, background: '#fff6e5', padding: '12px 14px' }}>
                  <span>
                    Клиент ещё не прислал: {missingTitles.join(', ')}. После отключения ссылка и QR перестанут работать;
                    уже присланное останется в анкете. Отключить?
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="button" fill onClick={closeLink}>
                      Отключить ссылку
                    </Button>
                    <Button type="button" onClick={() => setConfirmOff(false)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {allReceived ? (
                    <Button type="button" fill full onClick={closeLink}>
                      Готово, закрыть ссылку
                    </Button>
                  ) : (
                    <Button type="button" fill full onClick={() => setIsOpen(false)}>
                      Свернуть
                    </Button>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
                    <button type="button" style={linkButton} onClick={() => setLink(null)}>
                      Изменить набор документов
                    </button>
                    {!allReceived && (
                      <button type="button" style={{ ...linkButton, color: MUTED }} onClick={() => setConfirmOff(true)}>
                        Отключить ссылку
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ClientUploadBlock;
